import { useState, useEffect } from "react";
import { DefaultSidebar, Sidebar, THEME } from "@excalidraw/excalidraw";
import {
  messageCircleIcon,
  presentationIcon,
  LoadIcon,
  TrashIcon,
  PlusIcon,
} from "@excalidraw/excalidraw/components/icons";
import { LinkButton } from "@excalidraw/excalidraw/components/LinkButton";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";

import "./AppSidebar.scss";

import type { ProjectMetadata } from "../data/projectsStore";

const ProjectItem = ({
  project,
  isActive,
  index,
  isDragging,
  onSelect,
  onRename,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
}: {
  project: ProjectMetadata;
  isActive: boolean;
  index: number;
  isDragging: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(project.name);

  useEffect(() => {
    setEditName(project.name);
  }, [project.name]);

  const handleBlur = () => {
    setIsEditing(false);
    if (editName.trim() && editName.trim() !== project.name) {
      onRename(editName.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setEditName(project.name);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`project-item ${isActive ? "active" : ""}`}
      onClick={() => {
        if (!isActive) {
          onSelect();
        }
      }}
      draggable={!isEditing}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      style={{
        opacity: isDragging ? 0.4 : 1,
        cursor: isEditing ? "text" : "grab",
      }}
    >
      <div
        className="project-info"
        onDoubleClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        {isEditing ? (
          <input
            autoFocus
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              border: "1px solid var(--color-primary)",
              borderRadius: "4px",
              padding: "2px 4px",
              fontSize: "0.9rem",
              background: "var(--input-bg-color, #fff)",
              color: "var(--body-color, #000)",
            }}
          />
        ) : (
          <>
            <div className={`project-name ${isActive ? "active" : ""}`}>
              {project.name}
            </div>
            <div className="project-date">
              {new Date(project.updatedAt).toLocaleDateString()}{" "}
              {new Date(project.updatedAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </>
        )}
      </div>
      <div className="project-actions">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (
              confirm(
                `Tem certeza que deseja deletar o projeto "${project.name}"?`,
              )
            ) {
              onDelete();
            }
          }}
          title="Deletar Projeto"
        >
          {TrashIcon}
        </button>
      </div>
    </div>
  );
};

export const AppSidebar = ({
  projectsList,
  activeProjectId,
  onSelectProject,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onReorderProjects,
}: {
  projectsList: ProjectMetadata[];
  activeProjectId: string | null;
  onSelectProject: (id: string) => void;
  onCreateProject: () => void;
  onRenameProject: (id: string, name: string) => void;
  onDeleteProject: (id: string) => void;
  onReorderProjects: (list: ProjectMetadata[]) => void;
}) => {
  const { theme, openSidebar } = useUIAppState();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    const newList = [...projectsList];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);

    setDraggedIndex(index);
    onReorderProjects(newList);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <DefaultSidebar>
      <DefaultSidebar.TabTriggers>
        <Sidebar.TabTrigger
          tab="custom-projects"
          style={{ opacity: openSidebar?.tab === "custom-projects" ? 1 : 0.4 }}
          title="Meus Projetos"
        >
          {LoadIcon}
        </Sidebar.TabTrigger>
        <Sidebar.TabTrigger
          tab="comments"
          style={{ opacity: openSidebar?.tab === "comments" ? 1 : 0.4 }}
        >
          {messageCircleIcon}
        </Sidebar.TabTrigger>
        <Sidebar.TabTrigger
          tab="presentation"
          style={{ opacity: openSidebar?.tab === "presentation" ? 1 : 0.4 }}
        >
          {presentationIcon}
        </Sidebar.TabTrigger>
      </DefaultSidebar.TabTriggers>

      <Sidebar.Tab tab="custom-projects">
        <div className="projects-sidebar-container">
          <button className="projects-header-btn" onClick={onCreateProject}>
            {PlusIcon} Novo Projeto
          </button>
          <div className="projects-list">
            {projectsList.map((project, index) => (
              <ProjectItem
                key={project.id}
                index={index}
                project={project}
                isActive={project.id === activeProjectId}
                isDragging={index === draggedIndex}
                onSelect={() => onSelectProject(project.id)}
                onRename={(name) => onRenameProject(project.id, name)}
                onDelete={() => onDeleteProject(project.id)}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              />
            ))}
            {projectsList.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#888",
                  marginTop: "2rem",
                }}
              >
                Nenhum projeto encontrado.
              </div>
            )}
          </div>
        </div>
      </Sidebar.Tab>

      <Sidebar.Tab tab="comments">
        <div className="app-sidebar-promo-container">
          <div
            className="app-sidebar-promo-image"
            style={{
              ["--image-source" as any]: `url(/oss_promo_comments_${
                theme === THEME.DARK ? "dark" : "light"
              }.jpg)`,
              opacity: 0.7,
            }}
          />
          <div className="app-sidebar-promo-text">
            Make comments with Excalidraw+
          </div>
          <LinkButton
            href={`${
              import.meta.env.VITE_APP_PLUS_LP
            }/plus?utm_source=excalidraw&utm_medium=app&utm_content=comments_promo#excalidraw-redirect`}
          >
            Sign up now
          </LinkButton>
        </div>
      </Sidebar.Tab>
      <Sidebar.Tab tab="presentation" className="px-3">
        <div className="app-sidebar-promo-container">
          <div
            className="app-sidebar-promo-image"
            style={{
              ["--image-source" as any]: `url(/oss_promo_presentations_${
                theme === THEME.DARK ? "dark" : "light"
              }.svg)`,
              backgroundSize: "60%",
              opacity: 0.4,
            }}
          />
          <div className="app-sidebar-promo-text">
            Create presentations with Excalidraw+
          </div>
          <LinkButton
            href={`${
              import.meta.env.VITE_APP_PLUS_LP
            }/plus?utm_source=excalidraw&utm_medium=app&utm_content=presentations_promo#excalidraw-redirect`}
          >
            Sign up now
          </LinkButton>
        </div>
      </Sidebar.Tab>
    </DefaultSidebar>
  );
};
